import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import StarRating from './StarRating';
import { submitProductReviewThunk } from '../../store/actions/reviewActions';
import { selectAuthReady } from '../../store/actions/authActions';
import { useAppDispatch, useAppSelector } from '../../store/hooks';

const FormWrap = styled.form`
  margin-top: 2rem;
  padding: 2rem 2.5rem;
  background: hsla(24, 30%, 94%, 0.6);
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Head = styled.div`
  margin-bottom: 1.5rem;
`;

const H3 = styled.h3`
  margin: 0 0 0.25rem;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.35rem, 2vw + 1rem, 1.65rem);
  font-weight: 300;
`;

const Sub = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodyXs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Field = styled.div`
  margin-bottom: 1.25rem;
`;

const Lab = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
`;

const Row2 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.foreground};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  resize: vertical;
  min-height: 8rem;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.foreground};
  }
`;

const SelectField = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.foreground};
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-top: 0.5rem;
`;

const BtnPrimary = styled.button`
  padding: 0.65rem 1.25rem;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: none;
  background: ${({ theme }) => theme.colors.foreground};
  color: ${({ theme }) => theme.colors.cream};
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BtnGhost = styled.button`
  padding: 0.65rem 1.25rem;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: 1px solid ${({ theme }) => theme.colors.foreground};
  background: transparent;
  color: ${({ theme }) => theme.colors.foreground};
  cursor: pointer;
`;

const Err = styled.p`
  color: ${({ theme }) => theme.colors.terracottaDark};
  font-size: ${({ theme }) => theme.type.bodySm};
  margin: 0 0 0.75rem;
`;

const Info = styled.p`
  margin: 0;
  padding: 0.75rem 1rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  border: 1px dashed ${({ theme }) => theme.colors.border};
`;

const InlineLink = styled(Link)`
  color: inherit;
  font-weight: 500;
  text-decoration: underline;
`;

export default function ReviewForm({
  productSlugOrId,
  productName,
  onCancel,
  onSubmitted,
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loggedIn = useAppSelector(selectAuthReady);
  const [rating, setRating] = useState(0);
  const [titleVal, setTitleVal] = useState('');
  const [bodyVal, setBodyVal] = useState('');
  const [size, setSize] = useState('');
  const [fit, setFit] = useState('');
  const [recommend, setRecommend] = useState(true);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const slug = String(productSlugOrId || '').trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInfo('');
    if (!rating || !titleVal.trim() || !bodyVal.trim()) {
      setError(t('modamai.reviewForm.validationRequired'));
      return;
    }
    setError('');

    if (!slug) {
      setError(t('modamai.reviewForm.missingProduct'));
      return;
    }

    if (!loggedIn) return;
    setSubmitting(true);
    try {
      await dispatch(
        submitProductReviewThunk({
          slugOrId: slug,
          rating,
          title: titleVal.trim(),
          body: bodyVal.trim(),
          sizePurchased: size.trim(),
          fit,
          recommends: !!recommend,
          isAnonymous: !!isAnonymous,
        }),
      );
      setInfo(t('modamai.reviewForm.submitThankModeration'));
      onSubmitted?.();
      setRating(0);
      setTitleVal('');
      setBodyVal('');
      setSize('');
      setFit('');
      setRecommend(true);
      setIsAnonymous(false);
    } catch (err) {
      setError(typeof err?.message === 'string' ? err.message : t('modamai.reviewForm.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormWrap onSubmit={handleSubmit}>
      <Head>
        <H3>{t('modamai.reviews.writeReview')}</H3>
        {productName ? <Sub>{productName}</Sub> : null}
      </Head>

      {error ? <Err>{error}</Err> : null}
      {info ? <Info style={{ marginBottom: '1rem' }}>{info}</Info> : null}

      {!loggedIn ? (
        <Info style={{ marginBottom: '1rem' }}>
          <InlineLink
            to={`/login?redirect=${encodeURIComponent(
              typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/shop/all',
            )}`}
          >
            {t('modamai.auth.signIn')}
          </InlineLink>{' '}
          {t('modamai.reviewForm.verifiedReviewLead')}
        </Info>
      ) : null}

      <Field>
        <Lab>{t('modamai.reviewForm.labelRating')}</Lab>
        <StarRating rating={rating} size="lg" interactive onChange={setRating} />
      </Field>

      <CheckboxRow style={{ marginBottom: '1.25rem' }}>
        <input
          type="checkbox"
          checked={isAnonymous}
          onChange={(ev) => setIsAnonymous(ev.target.checked)}
        />
        <span>{t('modamai.reviewForm.postAnonymously')}</span>
      </CheckboxRow>

      <Field>
        <Lab>{t('modamai.reviewForm.labelHeadline')}</Lab>
        <Input value={titleVal} onChange={(ev) => setTitleVal(ev.target.value)} placeholder="" />
      </Field>

      <Field>
        <Lab>{t('modamai.reviewForm.labelComments')}</Lab>
        <TextArea value={bodyVal} onChange={(ev) => setBodyVal(ev.target.value)} placeholder="" rows={5} />
      </Field>

      <Row2>
        <Field style={{ marginBottom: 0 }}>
          <Lab>{t('modamai.reviewForm.labelSizePurchased')}</Lab>
          <Input value={size} onChange={(ev) => setSize(ev.target.value)} placeholder="" />
        </Field>
        <Field style={{ marginBottom: 0 }}>
          <Lab>{t('modamai.reviewForm.labelFit')}</Lab>
          <SelectField value={fit} onChange={(ev) => setFit(ev.target.value)}>
            <option value="">{t('modamai.reviewForm.fitSelectPlaceholder')}</option>
            <option value="Runs Small">{t('modamai.reviewForm.fitRunsSmall')}</option>
            <option value="True to Size">{t('modamai.reviewForm.fitTrueToSize')}</option>
            <option value="Runs Large">{t('modamai.reviewForm.fitRunsLarge')}</option>
          </SelectField>
        </Field>
      </Row2>

      <CheckboxRow style={{ marginBottom: '1.25rem' }}>
        <input
          type="checkbox"
          checked={recommend}
          onChange={(ev) => setRecommend(ev.target.checked)}
          aria-label={t('modamai.reviewForm.recommendAria')}
        />
        <span>{t('modamai.reviewForm.recommendLabel')}</span>
      </CheckboxRow>

      <Actions>
        <BtnPrimary type="submit" disabled={submitting || !loggedIn}>
          {submitting ? t('modamai.reviewForm.submitting') : t('modamai.reviewForm.submitReview')}
        </BtnPrimary>
        {onCancel ? (
          <BtnGhost type="button" onClick={onCancel}>
            {t('modamai.reviewForm.cancel')}
          </BtnGhost>
        ) : null}
      </Actions>
    </FormWrap>
  );
}
